const DEFAULT_REDIRECT = '/dashboard'
const MAX_REDIRECT_DEPTH = 8

function safeDecode(value) {
  if (!value) return ''
  try {
    return decodeURIComponent(value)
  } catch (e) {
    return value
  }
}

function normalizeHashPath(value) {
  const decoded = safeDecode(String(value || '').trim())
  if (!decoded) return ''
  const hashIndex = decoded.indexOf('#')
  if (hashIndex >= 0) {
    return decoded.slice(hashIndex + 1) || ''
  }
  return decoded
}

function getQueryValue(fullPath, key) {
  const queryIndex = fullPath.indexOf('?')
  if (queryIndex < 0) return ''
  const query = fullPath.slice(queryIndex + 1)
  const pairs = query.split('&')
  for (const pair of pairs) {
    const equalIndex = pair.indexOf('=')
    const rawKey = equalIndex >= 0 ? pair.slice(0, equalIndex) : pair
    if (safeDecode(rawKey) === key) {
      return equalIndex >= 0 ? pair.slice(equalIndex + 1) : ''
    }
  }
  return ''
}

export function normalizeLoginRedirect(value, fallback = DEFAULT_REDIRECT) {
  let current = normalizeHashPath(value)
  for (let index = 0; index < MAX_REDIRECT_DEPTH; index += 1) {
    current = safeDecode(normalizeHashPath(current))
    if (!current || current === '/') {
      return fallback
    }
    if (current.indexOf('://') >= 0 || current.indexOf('//') === 0) {
      return fallback
    }
    if (current.charAt(0) !== '/') {
      return fallback
    }
    const path = current.split('?')[0]
    if (path !== '/login') {
      return current
    }
    const nestedRedirect = getQueryValue(current, 'redirect')
    if (!nestedRedirect) {
      return fallback
    }
    current = nestedRedirect
  }
  return fallback
}

const legacyRedirectRoutes = [
  ['authorize/modulelist.jsp', '/system/modules'],
  ['authorize/rolelist.jsp', '/system/roles'],
  ['authorize/authrole.jsp', '/system/role-authorization'],
  ['approval/approvallist.jsp', '/approval'],
  ['camera/cameralist.jsp', '/cameras'],
  ['company/companylist.jsp', '/companies'],
  ['deliverable/deliverablelist.jsp', '/deliverables'],
  ['department/departmentlist.jsp', '/departments'],
  ['evaluation/projectevaluationmaterial.jsp', '/project-evaluations/material'],
  ['evaluation/projectevaluationlist.jsp', '/project-evaluations'],
  ['interview/interviewitemlist.action', '/project/interview-config'],
  ['interview/interviewitemlist.jsp', '/project/interview-config'],
  ['interview/liveinterviewlist.jsp', '/report-workbench'],
  ['login/register', '/register'],
  ['login/test', '/media-test'],
  ['performance/performancelist.jsp', '/performance'],
  ['person/personlist.jsp', '/persons'],
  ['project/distributeuser.jsp', '/project/members'],
  ['project/documentlist.jsp', '/documents'],
  ['project/processitemlist.jsp', '/process-items'],
  ['project/serviceitemlist.jsp', '/service-items'],
  ['project/videolist.jsp', '/videos'],
  ['project/projectlist.jsp', '/project/list'],
  ['room/roomlist.jsp', '/rooms'],
  ['share/sharemateriallist.jsp', '/shares'],
  ['statistics/statisticslist.action', '/statistics'],
  ['statistics/statisticslist.jsp', '/statistics'],
  ['template/templatelist.jsp', '/templates'],
  ['user/userlist.jsp', '/users']
]

export function resolvePostLoginRedirect(value) {
  const target = String(value || '')
  if (!target) {
    return { type: 'route', target: DEFAULT_REDIRECT }
  }

  if (/^\s*[A-Za-z][A-Za-z0-9+.-]*:/.test(target) || /^\s*\/\//.test(target)) {
    return { type: 'location', target }
  }

  const lower = target.toLowerCase()
  if (lower === '/index.jsp' || lower === 'index.jsp') {
    return { type: 'route', target: DEFAULT_REDIRECT }
  }
  if (lower.indexOf('/view/login/login') >= 0) {
    return { type: 'route', target: '/login' }
  }
  for (const mapping of legacyRedirectRoutes) {
    if (lower.indexOf(mapping[0]) >= 0) {
      return { type: 'route', target: appendOriginalQuery(mapping[1], target) }
    }
  }

  const isLegacyResource = lower.indexOf('/view/') >= 0 ||
    lower.indexOf('.action') >= 0 ||
    lower.indexOf('.jsp') >= 0
  if (!isLegacyResource && target.charAt(0) === '/' &&
      target.indexOf('://') < 0 && target.indexOf('//') !== 0) {
    return { type: 'route', target }
  }
  // The JSP controller passes every other non-empty redirect directly to
  // Spring's redirect: prefix, including relative and external locations.
  return { type: 'location', target }
}

function appendOriginalQuery(route, original) {
  const queryIndex = original.indexOf('?')
  return queryIndex < 0 ? route : `${route}${original.slice(queryIndex)}`
}

export function buildLoginLocation(redirect) {
  const normalized = normalizeLoginRedirect(redirect)
  return {
    path: '/login',
    query: normalized && normalized !== DEFAULT_REDIRECT ? { redirect: normalized } : {}
  }
}

export function isIgnoredNavigationError(error) {
  if (!error) return false
  const name = error.name || ''
  const message = error.message || ''
  return [
    'NavigationDuplicated',
    'NavigationCancelled',
    'NavigationRedirected'
  ].indexOf(name) >= 0 ||
    message.indexOf('Avoided redundant navigation') >= 0 ||
    message.indexOf('Navigation cancelled') >= 0 ||
    message.indexOf('Redirected when going from') >= 0
}

function stringifyLocation(location) {
  if (!location) return ''
  if (typeof location === 'string') return location
  if (location.path) {
    const query = location.query || {}
    const keys = Object.keys(query).filter(key => query[key] !== undefined && query[key] !== null && query[key] !== '')
    if (!keys.length) return location.path
    return `${location.path}?${keys.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`).join('&')}`
  }
  return ''
}

export function isSameRouteLocation(route, location) {
  const target = stringifyLocation(location)
  const current = route && route.fullPath
  return !!target && current === target
}

export function safeReplace(router, location) {
  if (!router || !location) return Promise.resolve()
  if (isSameRouteLocation(router.currentRoute, location)) {
    return Promise.resolve()
  }
  return router.replace(location).catch(error => {
    if (isIgnoredNavigationError(error)) {
      return error
    }
    // Keep real navigation errors visible while preventing expected router noise from reaching Vue overlay.
    // eslint-disable-next-line no-console
    console.error(error)
    return Promise.reject(error)
  })
}

export function safeNextRedirect(to, target, next) {
  const normalizedTarget = stringifyLocation(target)
  if (!normalizedTarget) {
    next()
    return
  }
  if (to && to.fullPath === normalizedTarget) {
    next()
    return
  }
  if (to && to.path === '/login' && normalizedTarget.split('?')[0] === '/login') {
    next()
    return
  }
  next(target)
}

export { DEFAULT_REDIRECT }
